import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/joy';
import { UploadSimple as Upload, X, FileText, Image, CheckCircle, XCircle as AlertCircle, ArrowCounterClockwise as RotateCcw } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';

interface DocumentUploadModalProps {
  open: boolean;
  onClose: () => void;
  jobId: number;
}

type FileUploadStatus = 'pending' | 'uploading' | 'success' | 'failed';

interface FileWithPreview {
  file: File;
  preview?: string;
  status?: FileUploadStatus;
  error?: string;
}

const ALLOWED_EXTENSIONS = [
  '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp',
  '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt',
  '.ppt', '.pptx', '.odt', '.ods', '.odp'
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  open,
  onClose,
  jobId,
}) => {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles: FileWithPreview[] = [];
    
    selectedFiles.forEach(file => {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`${file.name} exceeds 50MB limit`);
        return;
      }
      
      // Validate file extension
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setError(`${file.name} has unsupported file type`);
        return;
      }
      
      const fileWithPreview: FileWithPreview = { 
        file,
        status: 'pending'
      };
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          fileWithPreview.preview = e.target?.result as string;
          setFiles(prev => [...prev]);
        };
        reader.readAsDataURL(file);
      }
      
      validFiles.push(fileWithPreview);
    });
    
    setFiles(prev => [...prev, ...validFiles]);
    setError(null);
    setUploadSuccess(false);
  };

  const removeFile = (index: number) => {
    setFiles(prev => {
      const file = prev[index];
      // Don't allow removing successfully uploaded files
      if (file.status === 'success') {
        return prev;
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadFiles = async (filesToUpload: FileWithPreview[]) => {
    console.log('uploadFiles called with', filesToUpload.length, 'files');
    setUploading(true);
    setError(null);

    const totalFiles = filesToUpload.length;

    console.log('Files to process:', filesToUpload);
    console.log('Total files to upload:', totalFiles);

    if (totalFiles === 0) {
      console.log('No files to process, setting uploading to false');
      setUploading(false);
      return;
    }

    let completedCount = 0;

    // Process each file that needs to be uploaded
    for (let i = 0; i < filesToUpload.length; i++) {
      const fileToUpload = filesToUpload[i];
      console.log('Processing file:', fileToUpload.file.name);

      // Find the index in the files state by matching the file object
      const fileIndex = files.findIndex(f =>
        f.file.name === fileToUpload.file.name && f.file.size === fileToUpload.file.size
      );

      console.log('Found file at index:', fileIndex);

      if (fileIndex === -1) {
        console.log('File not found in state, skipping');
        continue; // File not found, skip
      }

      // Update status to uploading
      setFiles(prev => {
        const updated = [...prev];
        updated[fileIndex] = { ...updated[fileIndex], status: 'uploading', error: undefined };
        return updated;
      });

      const formData = new FormData();
      formData.append('file', fileToUpload.file);
      formData.append('job', jobId.toString());

      try {
        const response = await apiClient.post('/api/docs/', formData);
        
        // Update status to success
        setFiles(prev => {
          const updated = [...prev];
          updated[fileIndex] = { ...updated[fileIndex], status: 'success', error: undefined };
          return updated;
        });
        
        completedCount++;
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
        
        // Update status to failed with error message
        setFiles(prev => {
          const updated = [...prev];
          updated[fileIndex] = { ...updated[fileIndex], status: 'failed', error: errorMessage };
          return updated;
        });
        
        completedCount++;
      }

      // Update progress based on files being processed
      setUploadProgress((completedCount / totalFiles) * 100);
    }

    setUploading(false);
    
    // Check if all files succeeded
    setFiles(prev => {
      const allSucceeded = prev.length > 0 && prev.every(f => f.status === 'success');
      
      if (allSucceeded) {
        setUploadSuccess(true);
        // Refresh documents list
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['jobDocs', jobId.toString()] });
        }, 500);
        // Close modal after a short delay
        setTimeout(() => {
          setFiles([]);
          setError(null);
          setUploadSuccess(false);
          onClose();
        }, 1500);
      } else {
        // Refresh documents list for successful uploads
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['jobDocs', jobId.toString()] });
        }, 500);
      }
      
      return prev;
    });
  };

  const handleUpload = () => {
    console.log('handleUpload called');
    console.log('Current files state:', files);
    const pendingFiles = files.filter(f => f.status !== 'success');
    console.log('Pending files:', pendingFiles);
    if (pendingFiles.length === 0) {
      console.log('No pending files, returning early');
      return;
    }
    console.log('Calling uploadFiles with', pendingFiles.length, 'files');
    uploadFiles(pendingFiles);
  };

  const handleRetryFailed = () => {
    const failedFiles = files.filter(f => f.status === 'failed');
    if (failedFiles.length === 0) return;
    uploadFiles(failedFiles);
  };

  const handleRetryFile = (index: number) => {
    setFiles(prev => {
      const file = prev[index];
      if (file.status !== 'failed') return prev;
      
      const updated = [...prev];
      updated[index] = { ...updated[index], status: 'pending', error: undefined };
      
      // Start upload for this file
      setTimeout(() => {
        uploadFiles([updated[index]]);
      }, 0);
      
      return updated;
    });
  };

  const handleClose = () => {
    if (uploading) return;
    
    const hasPendingOrFailed = files.some(f => f.status === 'pending' || f.status === 'failed');
    
    if (hasPendingOrFailed && files.length > 0) {
      const pendingCount = files.filter(f => f.status === 'pending').length;
      const failedCount = files.filter(f => f.status === 'failed').length;
      
      if (window.confirm(
        `You have ${pendingCount > 0 ? `${pendingCount} pending` : ''}${pendingCount > 0 && failedCount > 0 ? ' and ' : ''}${failedCount > 0 ? `${failedCount} failed` : ''} upload${(pendingCount + failedCount) > 1 ? 's' : ''}. Are you sure you want to close?`
      )) {
        setFiles([]);
        setError(null);
        setUploadSuccess(false);
        onClose();
      }
    } else {
      setFiles([]);
      setError(null);
      setUploadSuccess(false);
      onClose();
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    const input = { target: { files: droppedFiles } } as any;
    handleFileSelect(input);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image size={20} />;
    }
    return <FileText size={20} />;
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog size="lg" sx={{ maxWidth: 600 }}>
        <ModalClose />
        <Typography level="h4">Upload Documents</Typography>
        
        {/* Error Display */}
        {error && (
          <Alert 
            color="danger" 
            sx={{ mt: 2 }}
            variant="soft"
          >
            <Typography level="body-sm">
              {error}
            </Typography>
          </Alert>
        )}

        {/* Success Message */}
        {uploadSuccess && (
          <Alert 
            color="success" 
            sx={{ mt: 2 }}
            variant="soft"
          >
            <Typography level="body-sm">
              All files uploaded successfully! Your documents are now available.
            </Typography>
          </Alert>
        )}

        {/* Retry Failed Files Banner */}
        {files.some(f => f.status === 'failed') && !uploading && (
          <Alert 
            color="warning" 
            sx={{ mt: 2 }}
            variant="soft"
            endDecorator={
              <Button
                size="sm"
                variant="soft"
                color="warning"
                onClick={handleRetryFailed}
                startDecorator={<RotateCcw size={16} />}
              >
                Retry Failed
              </Button>
            }
          >
            <Typography level="body-sm">
              {files.filter(f => f.status === 'failed').length} file{files.filter(f => f.status === 'failed').length !== 1 ? 's' : ''} failed to upload
            </Typography>
          </Alert>
        )}
        
        {/* Drop Zone */}
        <Box
          sx={{
            mt: 3,
            p: 4,
            border: '2px dashed',
            borderColor: 'neutral.300',
            borderRadius: 'md',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.400',
              backgroundColor: 'background.level1',
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <Upload size={48} color="gray" />
          <Typography level="body-lg" sx={{ mt: 2 }}>
            Drop files here or click to browse
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
            Supported formats: PDF, Images, Word, Excel, PowerPoint, CSV, TXT
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Maximum file size: 50MB
          </Typography>
          <input
            id="file-input"
            type="file"
            multiple
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
        
        {/* Selected Files */}
        {files.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography level="title-sm" sx={{ mb: 2 }}>
              Selected Files ({files.length})
              {files.filter(f => f.status === 'success').length > 0 && (
                <Typography component="span" level="body-sm" sx={{ color: 'success.500', ml: 1 }}>
                  ({files.filter(f => f.status === 'success').length} uploaded)
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {files.map((fileItem, index) => {
                const getStatusIcon = () => {
                  switch (fileItem.status) {
                    case 'success':
                      return <CheckCircle size={20} color="#10b981" />;
                    case 'failed':
                      return <AlertCircle size={20} color="#ef4444" />;
                    case 'uploading':
                      return <CircularProgress size="sm" />;
                    default:
                      return null;
                  }
                };

                const getBorderColor = () => {
                  switch (fileItem.status) {
                    case 'success':
                      return 'success.300';
                    case 'failed':
                      return 'danger.300';
                    case 'uploading':
                      return 'primary.300';
                    default:
                      return 'neutral.200';
                  }
                };

                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      border: '1px solid',
                      borderColor: getBorderColor(),
                      borderRadius: 'sm',
                      backgroundColor: fileItem.status === 'success' ? 'success.50' : 
                                     fileItem.status === 'failed' ? 'danger.50' : 'background.surface',
                    }}
                  >
                    {fileItem.preview ? (
                      <img
                        src={fileItem.preview}
                        alt={fileItem.file.name}
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : (
                      getFileIcon(fileItem.file)
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                          {fileItem.file.name}
                        </Typography>
                        {getStatusIcon()}
                      </Box>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {formatFileSize(fileItem.file.size)}
                      </Typography>
                      {fileItem.error && (
                        <Typography level="body-xs" sx={{ color: 'danger.600', mt: 0.5 }}>
                          {fileItem.error}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {fileItem.status === 'failed' && (
                        <Button
                          size="sm"
                          variant="plain"
                          color="primary"
                          onClick={() => handleRetryFile(index)}
                          disabled={uploading}
                          startDecorator={<RotateCcw size={14} />}
                        >
                          Retry
                        </Button>
                      )}
                      {fileItem.status !== 'success' && (
                        <Button
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => removeFile(index)}
                          disabled={uploading}
                        >
                          <X size={16} />
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
        
        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.level1', borderRadius: 'md' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                Uploading files...
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {Math.round(uploadProgress)}%
              </Typography>
            </Box>
            <LinearProgress 
              determinate 
              value={uploadProgress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                }
              }} 
            />
            <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 1, textAlign: 'center' }}>
              Please wait while your files are being uploaded
            </Typography>
          </Box>
        )}
        
        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'flex-end' }}>
          <Button
            variant="plain"
            color="neutral"
            onClick={handleClose}
            disabled={uploading}
          >
            {files.every(f => f.status === 'success') ? 'Close' : 'Cancel'}
          </Button>
          {files.some(f => f.status !== 'success') && (
            <Button
              variant="solid"
              color="primary"
              onClick={handleUpload}
              disabled={files.filter(f => f.status !== 'success').length === 0 || uploading}
              startDecorator={uploading ? <CircularProgress size="sm" /> : <Upload size={16} />}
            >
              {uploading 
                ? 'Uploading...' 
                : `Upload ${files.filter(f => f.status !== 'success').length} File${files.filter(f => f.status !== 'success').length !== 1 ? 's' : ''}`
              }
            </Button>
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
};